import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth, useLogout, postAuthPath } from '@/hooks/use-auth'
import { completeOnboarding } from '@/lib/auth'
import { ApiError, getApiErrorMessage } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'

export function CompleteProfilePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, isLoading } = useAuth()
  const logout = useLogout()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('+254')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const errorRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (isLoading) return
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    if (!user.requiresOnboarding) {
      navigate(postAuthPath(user), { replace: true })
      return
    }
    setName(user.name)
    setEmail(user.email ?? '')
    setPhone(user.phone || '+254')
  }, [user, isLoading, navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setFieldErrors({})

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match')
      setFieldErrors({ confirmPassword: 'Passwords do not match' })
      return
    }

    setSubmitting(true)
    try {
      const { user: updated } = await completeOnboarding(
        name,
        email,
        phone,
        password,
        confirmPassword,
      )
      await queryClient.invalidateQueries({ queryKey: ['auth'] })
      navigate(postAuthPath(updated), { replace: true })
    } catch (err) {
      const message = getApiErrorMessage(err, 'Could not complete profile')
      const nextFieldErrors =
        err instanceof ApiError && Object.keys(err.fieldErrors).length > 0
          ? err.fieldErrors
          : inferFieldErrors(message)

      setSubmitError(message)
      setFieldErrors(nextFieldErrors)
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    } finally {
      setSubmitting(false)
    }
  }

  function clearFieldError(field: string) {
    setFieldErrors((current) => {
      if (!(field in current)) return current
      const next = { ...current }
      delete next[field]
      return next
    })
    setSubmitError(null)
  }

  async function handleSignOut() {
    await logout.mutateAsync().catch(() => undefined)
    navigate('/login', { replace: true })
  }

  if (isLoading || !user?.requiresOnboarding) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream font-sans text-sm text-muted-foreground">
        Loading…
      </div>
    )
  }

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />

      <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">
        Complete your profile
      </h2>
      <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
        Add your email, confirm your details, and choose a new password before accessing the portal.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label="Full name"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            clearFieldError('name')
          }}
          error={fieldErrors.name}
          autoComplete="name"
          required
        />
        <Input
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            clearFieldError('email')
          }}
          error={fieldErrors.email}
          autoComplete="email"
          required
        />
        <Input
          label="Phone number"
          type="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value)
            clearFieldError('phone')
          }}
          error={fieldErrors.phone}
          hint="Format: +254XXXXXXXXX"
          autoComplete="tel"
          required
        />
        <Input
          label="New password"
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            clearFieldError('password')
          }}
          error={fieldErrors.password}
          autoComplete="new-password"
          hint="At least 8 characters with uppercase, lowercase and a number"
          required
        />
        <Input
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value)
            clearFieldError('confirmPassword')
          }}
          error={fieldErrors.confirmPassword}
          autoComplete="new-password"
          required
        />

        {submitError && (
          <p
            ref={errorRef}
            className="rounded border border-destructive/30 bg-status-overdue-bg px-3 py-2 font-sans text-sm text-destructive"
            role="alert"
          >
            {submitError}
          </p>
        )}

        <Button type="submit" className="w-full" size="lg" loading={submitting}>
          Save and continue
        </Button>
      </form>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={handleSignOut}
          className="font-sans text-sm text-muted-foreground transition-colors hover:text-primary"
        >
          Sign out
        </button>
      </div>
    </AuthCard>
  )
}

function inferFieldErrors(message: string): Record<string, string> {
  const lower = message.toLowerCase()
  if (lower.includes('email')) return { email: message }
  if (lower.includes('phone')) return { phone: message }
  if (lower.includes('password')) return { password: message }
  if (lower.includes('name')) return { name: message }
  return {}
}
